import React from "react";
import { Typography, Image } from "antd";
import {
  PageFooterProps,
  QuickLinkItem,
  FooterInfo,
  QuickLinkSection,
} from "./type";
import "./index.scss";

const { Link } = Typography;
const DEFAULT_FOOTER_INFO: FooterInfo = {
  logo: "",
  info: [],
};
const DEFAULT_QUICK_LINKS: QuickLinkSection[] = [];
const DEFAULT_PLACEHOLDER =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// 二维码组件
const QRCodeSection: React.FC<{ items: QuickLinkItem[] }> = ({ items }) => (
  <div className="qrcode-section">
    {items.map((item, index) => (
      <div key={`qrcode-${index}`} className="qrCode">
        <Image
          src={item.url}
          alt={item.title}
          width={120}
          height={120}
          preview={false}
        />
        <div className="qr-code-text">
          <span>{item.title}</span>
          {item.subTitle && <span>{item.subTitle}</span>}
        </div>
      </div>
    ))}
  </div>
);

// 链接组件
const LinkSection: React.FC<{
  section: QuickLinkSection;
  onLinkClick?: (item: QuickLinkItem, e?: React.MouseEvent) => void;
}> = ({ section, onLinkClick }) => (
  <div className={`linkSection ${section.className || ""}`}>
    {section.title && (
      <div className="section-title">
        <span className="sectionTitle">{section.title}</span>
        <div className="section-line" />
      </div>
    )}
    <div className="linkContent">
      {section.content.map((item, index) => (
        <div key={`link-${index}`} className="linkItem">
          <Link
            href={item.url}
            target="_blank"
            className="link-item"
            onClick={(e) => onLinkClick?.(item, e)}
          >
            {item.icon && <img src={item.icon} alt="" className="link-icon" />}
            {item.title}
          </Link>
        </div>
      ))}
    </div>
  </div>
);

// 页脚底部组件（备案号等）
const FooterBottom: React.FC<{ footerInfo: FooterInfo }> = ({ footerInfo }) => {
  const [imgError, setImgError] = React.useState(false);
  const logoUrl = footerInfo?.logo || DEFAULT_PLACEHOLDER;

  return (
    <div className="footer-bottom">
      <div className="footer-logo-wrapper">
        {!imgError && (
          <img
            alt=""
            src={logoUrl}
            className="footer-logo"
            onError={() => setImgError(true)}
          />
        )}
      </div>
      <div className="footer-info-group">
        {footerInfo?.info?.map((item, index) => (
          <div key={`footer-info-${index}`} className="footer-info-item">
            {item.icon?.map((icon, iconIndex) => (
              <img
                key={`icon-${iconIndex}`}
                alt=""
                src={icon}
                className="footer-info-icon"
              />
            ))}
            <span
              style={{ cursor: item.url ? "pointer" : "default" }}
              onClick={() => {
                if (item.url) {
                  window.open(item.url, "_blank");
                }
              }}
            >
              {item.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// 页脚组件（主）
const PageFooter: React.FC<PageFooterProps> = ({
  wrapperClassName = "",
  quickLinks = DEFAULT_QUICK_LINKS,
  footerInfo = DEFAULT_FOOTER_INFO,
  picture = "",
  onLinkClick,
}) => {
  const safeQuickLinks = Array.isArray(quickLinks)
    ? quickLinks
    : DEFAULT_QUICK_LINKS;
  const safeFooterInfo = footerInfo || DEFAULT_FOOTER_INFO;

  return (
    <footer className={`footer ${wrapperClassName}`}>
      {picture && <img src={picture} alt="" className="footer-img" />}
      <div className="container">
        <div className="quickLinks">
          {safeQuickLinks.map((section, index) => {
            if (!section || !Array.isArray(section.content)) {
              return null;
            }

            const firstItem = section.content[0];
            return (
              <React.Fragment key={`section-${index}`}>
                {firstItem?.type === "qrCode" ? (
                  <QRCodeSection items={section.content} />
                ) : (
                  <LinkSection section={section} onLinkClick={onLinkClick} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <FooterBottom footerInfo={safeFooterInfo} />
    </footer>
  );
};

const Footer = () => (
  <PageFooter quickLinks={quickLinks} footerInfo={footerInfo} />
);

export { PageFooter, Footer };

// 入驻指南：https://help.daqihui.com/1.0/manual/quick-start/qcs/buy-service.html
// 企采商高级版：https://www.daqihui.com/go/portal/becomeSupplier
// 操作手册：https://help.daqihui.com/1.0/manual/
// 大企汇数智化采购：https://www.daqihui.com/go/portal/becomeBuyer
// 操作手册：https://help.daqihui.com/1.0/manual/quick-start/dqc/operating%20manual/Inquiry%20and%20comparison.html
// 常见问题：https://help.daqihui.com/1.0/manual/service/qa.html
// 开放平台：https://help.daqihui.com/1.0/manual/open/solution/buyer-solution.html
// 服务条款：改成保密协议，放https://help.daqihui.com/1.0/manual/rule-notice/website-rule/xydjbmxyxq.html
// 违规处理规则：https://help.daqihui.com/1.0/manual/rule-notice/website-rule/quotation-rule.html
// 新闻公告：https://help.daqihui.com/1.0/manual/rule-notice/notice/20250330-service-change.html
// 联系我们：https://help.daqihui.com/1.0/manual/service/contact.html
// 公司介绍：https://terminus.io/about-us/intro
// 成功案例：https://terminus.io/cases

const quickLinks = [
  {
    title: "供应商指南",
    content: [
      {
        title: "入驻指南",
        type: "jumpMode",
        url: `${process.env.HELP_URL}/quick-start/qcs/buy-service.html`,
      },
      {
        title: "高级企采商",
        type: "jumpMode",
        url: `${process.env.YCX_HOME}/become-supplier`,
      },
      {
        title: "操作手册",
        type: "jumpMode",
        url: `${process.env.HELP_URL}`,
      },
      {
        title: "工品管家",
        type: "jumpMode",
        url: `${process.env.YCX_HOME}/steward`,
      },
    ],
  },
  {
    title: "采购商指南",
    content: [
      {
        title: "大企汇数智化采购",
        type: "jumpMode",
        url: `${process.env.YCX_HOME}/become-buyer`,
      },
      {
        title: "操作手册",
        type: "jumpMode",
        url: `${process.env.HELP_URL}/quick-start/dqc/operating%20manual/Inquiry%20and%20comparison.html`,
      },
      {
        title: "常见问题",
        type: "jumpMode",
        url: `${process.env.HELP_URL}/service/qa.html`,
      },
      {
        title: "开放平台",
        type: "jumpMode",
        url: `${process.env.HELP_URL}/open/solution/buyer-solution.html`,
      },
    ],
  },
  {
    title: "平台规则",
    content: [
      {
        title: "服务条款",
        type: "jumpMode",
        url: `${process.env.HELP_URL}/rule-notice/website-rule/xydjbmxyxq.html`,
      },
      {
        title: "违规处理规则",
        type: "jumpMode",
        url: `${process.env.HELP_URL}/rule-notice/website-rule/quotation-rule.html`,
      },
    ],
  },
  {
    title: "关于我们",
    content: [
      {
        title: "新闻公告",
        type: "jumpMode",
        url: `${process.env.HELP_URL}/rule-notice/notice/20250330-service-change.html`,
      },
      {
        title: "联系我们",
        type: "jumpMode",
        url: `${process.env.HELP_URL}/service/contact.html`,
      },
      {
        title: "公司介绍",
        type: "jumpMode",
        url: "https://terminus.io/about-us/intro",
      },
      {
        title: "成功案例",
        type: "jumpMode",
        url: "https://terminus.io/cases",
      },
    ],
  },
  {
    content: [
      {
        type: "qrCode",
        title: "端点科技",
        subTitle: "微信公众号",
        url: "http://terminus-emp.oss-cn-hangzhou.aliyuncs.com/trantor/attachments/5775b350-3385-4f2b-bac1-d10f1f7f631f.jpg",
      },
    ],
  },
  {
    content: [
      {
        type: "qrCode",
        title: "大企汇",
        subTitle: "微信公众号",
        url: "https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/svg/daqihuiwx.svg",
      },
    ],
  },
];
const footerInfo = {
  logo: "https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/svg/logo-Black.svg",
  info: [
    {
      icon: [
        "https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/footer-logo1.png",
        "https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/footer-logo2.png",
      ],
      title: "浙公网安备 33010802003150号",
      url: "https://beian.mps.gov.cn/#/query/webSearch?code=33010802003150",
    },
    {
      title: "备案/许可证号：浙ICP备13004315号-9",
      url: "https://beian.miit.gov.cn/",
    },
    {
      title: "增值电信业务经营许可证：合字B2-20250574",
    },
  ],
};
